import { useState } from 'react';
import { Save, Building2, Bell, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { useAdmin } from '@/contexts/AdminContext';

export default function AdminSettings() {
  const { user, hasLDA } = useAdmin();
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    siteName: 'Dental Platform',
    email: user?.email || '',
    phone: '',
    bookingEmail: true,
    newEnrolment: true,
    lowSeats: true,
    weeklyDigest: false,
    maintenanceMode: false,
    registrationOpen: true,
    depositRequired: true,
    refundDays: 30,
  });

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2500); };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Admin</p>
        <h2 className="text-2xl lg:text-3xl font-playfair font-medium text-stone-900 tracking-tight mt-1">Settings</h2>
        <p className="text-stone-500 text-sm mt-1">Configure site settings and preferences.</p>
      </div>

      <Card className="border-stone-200 shadow-sm">
        <CardContent className="p-6 space-y-5">
          <h3 className="text-sm font-medium text-stone-900 flex items-center gap-2"><Building2 className="w-4 h-4 text-stone-400" /> Site Profile</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-2"><Label className="text-xs">Site Name</Label><Input value={form.siteName} onChange={e => update('siteName', e.target.value)} className="h-10" /></div>
            <div className="space-y-2"><Label className="text-xs">Contact Email</Label><Input value={form.email} onChange={e => update('email', e.target.value)} className="h-10" /></div>
            <div className="space-y-2"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => update('phone', e.target.value)} className="h-10" /></div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-stone-200 shadow-sm">
        <CardContent className="p-6 space-y-5">
          <h3 className="text-sm font-medium text-stone-900 flex items-center gap-2"><Bell className="w-4 h-4 text-stone-400" /> Notifications</h3>
          {[
            { key: 'bookingEmail', label: 'Booking confirmations', desc: 'Email when a deposit is paid' },
            { key: 'weeklyDigest', label: 'Weekly digest', desc: 'Summary of bookings and revenue' },
            ...(hasLDA ? [
              { key: 'newEnrolment', label: 'New enrolments', desc: 'Notified when a student registers' },
              { key: 'lowSeats', label: 'Low seat alerts', desc: 'Alert when ≤2 seats remain' },
            ] : []),
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between gap-4">
              <div><p className="text-sm font-medium text-stone-800">{item.label}</p><p className="text-xs text-stone-400">{item.desc}</p></div>
              <Switch checked={form[item.key]} onCheckedChange={v => update(item.key, v)} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-stone-200 shadow-sm">
        <CardContent className="p-6 space-y-5">
          <h3 className="text-sm font-medium text-stone-900 flex items-center gap-2"><Shield className="w-4 h-4 text-stone-400" /> Policies</h3>
          <div className="flex items-center justify-between"><div><p className="text-sm font-medium text-stone-800">Deposit required</p><p className="text-xs text-stone-400">Require deposit for bookings</p></div><Switch checked={form.depositRequired} onCheckedChange={v => update('depositRequired', v)} /></div>
          <div className="flex items-center justify-between"><div><p className="text-sm font-medium text-stone-800">Maintenance mode</p><p className="text-xs text-stone-400">Temporarily disable public site</p></div><Switch checked={form.maintenanceMode} onCheckedChange={v => update('maintenanceMode', v)} /></div>
          <div className="max-w-[200px] space-y-2"><Label className="text-xs">Refund window (days)</Label><Input type="number" value={form.refundDays} onChange={e => update('refundDays', parseInt(e.target.value) || 0)} className="h-10" /></div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} className="h-11 gap-2 bg-stone-900 hover:bg-stone-800 text-white"><Save className="w-4 h-4" /> {saved ? 'Saved ✓' : 'Save Settings'}</Button>
    </div>
  );
}
